import { ComponentPropsWithoutRef, ReactNode, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Datepicker, Textarea, Tag, InputField, Select } from '@ets/design-system';

import SearchUser, { UserOption } from '_common/components/SearchUser/SearchUser';
import { useEditDocumentMetadataMutation } from 'App/redux/MetadataApi';
import dayjs from 'dayjs';

import styles from './MetadataFields.module.scss';
import UsernameLabel from '../Labels/UsernameLabel/UsernameLabel';

type MetadataFieldsProps = {
  validations?: string;
  metadata: ApiSchemas['MetadataObjectSchema'];
  element: doDOC.Document | doDOC.File | doDOC.PDF | doDOC.Presentation;
  setValidations: (value: string) => void;
  exportVeeva?: boolean;
};

const MetadataFields = ({
  validations,
  metadata,
  element,
  setValidations,
}: MetadataFieldsProps) => {
  const intl = useIntl();
  const [editDocumentMetadata] = useEditDocumentMetadataMutation();

  const isRequired = metadata.required;

  //@ts-expect-error Check if the other types of elements will have metadata
  const documentMetadata = useMemo(() => element?.metadata?.[metadata.id], [element, metadata.id]);

  const [contentName, setContentName] = useState(
    documentMetadata && !Array.isArray(documentMetadata) ? documentMetadata : '',
  );
  const [date, setDate] = useState<dayjs.Dayjs | null>(
    documentMetadata && !Array.isArray(documentMetadata) && dayjs(documentMetadata).isValid()
      ? dayjs(documentMetadata)
      : null,
  );
  const [contentNameFeedback, setContentNameFeedback] = useState(
    isRequired && !contentName
      ? intl.formatMessage({ id: 'REQUIRED_FIELD_IS_EMPTY_PLEASE_ENTER_VALUE' })
      : false,
  );
  const [dateFeedback, setDateFeedback] = useState(
    isRequired && !date
      ? intl.formatMessage({ id: 'REQUIRED_FIELD_IS_EMPTY_PLEASE_ENTER_VALUE' })
      : false,
  );
  const handleContentNameChange: ComponentPropsWithoutRef<typeof Textarea>['onChange'] = (e) => {
    const target = e.target as HTMLTextAreaElement;
    const value = target.value;
    setContentName(value);
    if (isRequired && value.length < 1) {
      setContentNameFeedback(
        intl.formatMessage({ id: 'REQUIRED_FIELD_IS_EMPTY_PLEASE_ENTER_VALUE' }),
      );
    } else if (metadata.data_type === 'string' && value?.length > metadata.char_limit) {
      setValidations(
        intl.formatMessage({ id: 'EXCEEDED_CHARACTER_LIMIT' }, { value: metadata.char_limit }),
      );
      setContentNameFeedback(
        intl.formatMessage({ id: 'EXCEEDED_CHARACTER_LIMIT' }, { value: metadata.char_limit }),
      );
    } else {
      setValidations('');
      setContentNameFeedback(false);
      handleUpdateField(metadata.id, {
        value,
      });
    }
  };

  const handleDateChange = (newDate: Date | null) => {
    setDate(newDate ? dayjs(newDate) : null);
    handleUpdateField(metadata.id, { value: newDate?.toISOString() ?? '' });

    if (isRequired && !newDate) {
      setDateFeedback(intl.formatMessage({ id: 'REQUIRED_FIELD_IS_EMPTY_PLEASE_ENTER_VALUE' }));
    } else {
      setDateFeedback(false);
    }
  };
  const [selectedOption, setSelectedOption] = useState<{
    value: string | string[] | string[][] | undefined;
    label: ReactNode;
    type?: string;
  }>(
    Array.isArray(documentMetadata)
      ? {
          value: documentMetadata.join('-'),
          label:
            metadata.data_type === 'user' && !Array.isArray(documentMetadata) ? (
              <UsernameLabel userId={documentMetadata} />
            ) : (
              documentMetadata.join('-')
            ),
        }
      : {
          value: documentMetadata,
          label: <UsernameLabel userId={documentMetadata} />,
          type: metadata.data_type === 'user' ? 'user' : '',
        },
  );

  const handleUpdateField = (id: ObjectId, value: { value: string; remove?: boolean }) => {
    if (!validations) {
      editDocumentMetadata({
        element_type: element.type,
        document_id: element.id,
        field: id,
        value: value.value ?? '',
        add: !value.remove,
      });
    }
  };

  const selectOptions = useMemo(() => {
    if (metadata.data_type === 'multi-select') {
      return metadata?.options
        .filter((option) =>
          Array.isArray(documentMetadata)
            ? !documentMetadata
                .map((docOption) => JSON.stringify(docOption))
                .includes(JSON.stringify(option.fields))
            : true,
        )
        .map((option) => ({
          label: option.fields.join('-'),
          value: option.fields.join('-'),
          valueArray: option.fields,
        }));
    } else if (metadata.data_type === 'select') {
      return metadata?.options.map((option) => ({
        label: option.fields.join('-'),
        value: option.fields.join('-'),
        valueArray: option.fields,
      }));
    }
  }, [metadata, documentMetadata]);

  const selectValue = useMemo(
    () =>
      selectedOption?.value && !Array.isArray(selectedOption.value)
        ? { value: selectedOption.value, label: selectedOption.label, valueArray: [''] }
        : null,
    [selectedOption],
  );

  const fieldName = metadata?.name.replace(' ', '-').toLowerCase();
  const fieldTestId = `edit-metadata-${fieldName}`;
  switch (metadata.data_type) {
    case 'user':
      return (
        <InputField
          size="l"
          label={metadata?.name}
          extra={isRequired && intl.formatMessage({ id: 'REQUIRED' })}
          feedbackContent={
            isRequired &&
            !selectedOption?.value &&
            intl.formatMessage({ id: 'REQUIRED_FIELD_IS_EMPTY_PLEASE_SELECT_OPTION' })
          }
          testId={fieldTestId}
        >
          <SearchUser
            width="100%"
            size="l"
            menuPosition="fixed"
            placeholder={intl.formatMessage({ id: 'SELECT_A_DODOC_USER' })}
            hideSelectedOptions={false}
            value={
              selectedOption?.value && !Array.isArray(selectedOption.value)
                ? ({
                    value: selectedOption.value,
                    label: selectedOption.label,
                    type: selectedOption.type,
                  } as UserOption)
                : null
            }
            onChange={(value) => {
              setSelectedOption(value ?? { label: '', value: '', type: '' });
              if (value) {
                handleUpdateField(metadata.id, value);
              } else {
                handleUpdateField(metadata.id, { value: '' });
              }
            }}
            hasError={isRequired && !selectedOption?.value}
            testId={fieldTestId}
            menuLabel={intl.formatMessage({ id: 'START_TYPING_TO_SEE_THE_LIST_OF_USERS' })}
          />
        </InputField>
      );

    case 'multi-select':
    case 'select':
      return (
        <InputField
          size="l"
          label={metadata?.name}
          extra={isRequired && intl.formatMessage({ id: 'REQUIRED' })}
          feedbackContent={
            isRequired &&
            (!selectedOption || !selectedOption.value || documentMetadata?.length === 0) &&
            intl.formatMessage({ id: 'REQUIRED_FIELD_IS_EMPTY_PLEASE_SELECT_OPTION' })
          }
          testId={fieldTestId}
        >
          <>
            <Select
              variant="standard"
              isClearable={false}
              menuPosition="fixed"
              isSearchable
              size="l"
              width="100%"
              value={metadata.data_type !== 'multi-select' ? selectValue : null}
              options={selectOptions}
              onChange={(value) => {
                setSelectedOption(value ?? { label: '', value: '' });
                handleUpdateField(metadata.id, {
                  value: JSON.stringify(value?.valueArray ?? []),
                });
              }}
              placeholder={
                metadata.automatic
                  ? intl.formatMessage({
                      id: `SELECT_${metadata?.name.split(' ')[0].toUpperCase()}`,
                    })
                  : intl.formatMessage(
                      {
                        id: `SELECT_SOMETHING`,
                      },
                      { label: metadata?.name },
                    )
              }
              hasError={isRequired && !selectedOption?.value}
              testId={fieldTestId}
            />
            {metadata.data_type === 'multi-select' && Array.isArray(documentMetadata) && (
              <div className={styles.multiSelectValues}>
                {documentMetadata.map(
                  (option) =>
                    Array.isArray(option) && (
                      <Tag
                        key={option.join('-')}
                        testId={`multi-select-${option.join('-')}`}
                        variant="primary"
                        onClick={() =>
                          handleUpdateField(metadata.id, {
                            value: JSON.stringify(option ?? []),
                            remove: true,
                          })
                        }
                        label={option.join('-')}
                        size="m"
                      />
                    ),
                )}
              </div>
            )}
          </>
        </InputField>
      );
    case 'string':
      return (
        <InputField
          size="l"
          label={metadata.name}
          extra={isRequired && intl.formatMessage({ id: 'REQUIRED' })}
          feedbackContent={contentNameFeedback}
          testId={fieldTestId}
        >
          <Textarea
            plainText
            type="text"
            name="metadata"
            size="m"
            placeholder={intl.formatMessage(
              {
                id: 'INSERT_HERE',
              },
              {
                value: metadata?.name,
              },
            )}
            onChange={handleContentNameChange}
            initialContent={contentName}
            hasError={!!contentNameFeedback}
            onBlur={() =>
              handleUpdateField(metadata.id, {
                value: contentName,
              })
            }
            testId={`${fieldTestId}-textarea`}
          />
        </InputField>
      );
    case 'date':
      return (
        <InputField
          size="l"
          label={metadata.name}
          extra={isRequired && intl.formatMessage({ id: 'REQUIRED' })}
          feedbackContent={dateFeedback}
          testId={fieldTestId}
        >
          <Datepicker
            size="l"
            placeholderText={intl.formatMessage(
              {
                id: 'SELECT_SOMETHING',
              },
              {
                label: metadata?.name,
              },
            )}
            onChange={handleDateChange}
            selected={date?.toDate()}
            hasError={!!dateFeedback}
            testId={`${fieldTestId}-date`}
          />
        </InputField>
      );
    default:
      return null;
  }
};

export default MetadataFields;
